import React, { useMemo } from 'react';
import { formatNumber, formatPct } from './biTheme';
import { getFunnelStageMeta } from './funnelStageMeta';

const DeltaCell = ({ value }) => {
  if (value == null || !Number.isFinite(value)) {
    return <span className="text-sm text-slate-300">—</span>;
  }
  const up = value >= 0;
  return (
    <span className={`text-sm font-bold tabular-nums ${up ? 'text-emerald-600' : 'text-rose-500'}`}>
      {up ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
    </span>
  );
};

/**
 * Row-aligned conversion funnel so STEP / bar / USERS share the same row height.
 */
const VerticalWavyFunnel = ({ stages = [], bottleneckKey = null }) => {
  const maxCount = useMemo(
    () => Math.max(...stages.map((stage) => Number(stage.count) || 0), 1),
    [stages]
  );

  if (!stages.length) {
    return <p className="text-base text-gray-500">No funnel stages in this range.</p>;
  }

  const hotKey = bottleneckKey || stages[1]?.key || null;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <div className="min-w-[40rem]">
        <div className="grid grid-cols-[minmax(10rem,1.2fr)_minmax(12rem,1.6fr)_6rem_7rem_7rem] gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold uppercase tracking-wide text-slate-500">
          <span>Step</span>
          <span className="text-center">Conversion</span>
          <span className="text-right">Users</span>
          <span className="text-right">vs prev</span>
          <span className="text-right">vs avg</span>
        </div>

        {stages.map((stage, index) => {
          const meta = getFunnelStageMeta(stage.key);
          const Icon = meta.icon;
          const isHot = stage.key === hotKey;
          const value = Math.max(0, Number(stage.count) || 0);
          const widthPct = value > 0
            ? Math.max(2, Number(((value / maxCount) * 100).toFixed(1)))
            : 0;
          const pct = index === 0
            ? (value > 0 ? 100 : 0)
            : Number(stage.stepConversion || 0);
          const hasBar = value > 0 && Number.isFinite(pct) && pct > 0;
          const botInset = widthPct >= 12
            ? Math.max(4, Math.round((100 - widthPct * 0.82) / 2))
            : 2;

          return (
            <div
              key={stage.key}
              className="grid grid-cols-[minmax(10rem,1.2fr)_minmax(12rem,1.6fr)_6rem_7rem_7rem] items-center gap-3 border-b border-slate-100 px-3 last:border-b-0"
              style={{ minHeight: '3.75rem' }}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border shadow-sm"
                  style={{
                    background: `linear-gradient(145deg, rgba(255,255,255,0.95), ${meta.color}18)`,
                    borderColor: `${meta.color}33`,
                    color: meta.color,
                  }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.25} />
                </span>
                <p className="truncate text-base font-semibold text-slate-800">{stage.label}</p>
              </div>

              <div className="relative flex h-12 items-center justify-center">
                {hasBar ? (
                  <div
                    className="absolute inset-y-1 left-1/2 -translate-x-1/2"
                    style={{
                      width: `${widthPct}%`,
                      background: isHot
                        ? 'linear-gradient(90deg, #38bdf8, #6366f1, #7c3aed)'
                        : 'linear-gradient(90deg, rgba(196,181,253,0.5), rgba(165,180,252,0.42), rgba(147,197,253,0.48))',
                      clipPath: `polygon(0 0, 100% 0, ${100 - botInset}% 100%, ${botInset}% 100%)`,
                      borderRadius: 4,
                    }}
                  />
                ) : null}
                <span
                  className={`relative z-[1] rounded-md px-2.5 py-0.5 text-sm font-bold tabular-nums ${
                    hasBar && isHot
                      ? 'text-white'
                      : hasBar
                        ? 'bg-white/40 text-slate-800'
                        : 'text-slate-400'
                  }`}
                >
                  {formatPct(pct, pct >= 100 ? 0 : 1)}
                </span>
              </div>

              <p className="text-right text-lg font-bold tabular-nums text-slate-900">
                {formatNumber(stage.count)}
              </p>
              <div className="text-right">
                <DeltaCell value={stage.vsPrevPeriod} />
              </div>
              <div className="text-right">
                <DeltaCell value={stage.vsAvgPrev4} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VerticalWavyFunnel;
